import React from 'react';
import T from './theme';
import CollapsibleCard from './CollapsibleCard';
import InfoBubble from './InfoBubble';

export const DURABILITY_INFO =
  'How much the second half of a long easy run costs extra heartbeats for the same GAP speed. Lower is better. Around 0–2% is holding. Not leftover fatigue and not a Garmin recovery number. Only your easy/long runs with HR, 90+ min or 12+ miles; needs four of those.';

export const DROP60_INFO =
  'After a run, stand still 60 seconds with the watch still recording — auto-pause off. The number is how many beats your heart rate falls in that first minute after you stop. Bigger is quicker recovery. Not leftover hours and not resting HR.';

const M_PER_MILE = 1609.344;

function durabilityLine(d) {
  if (!d || !d.n) {
    return 'Need long easy/long runs with HR (≥90 min) to estimate durability.';
  }
  if (!d.usable) {
    return `${d.n} of ${d.need} long runs with HR — durability still settling.`;
  }
  const fade = d.decouple_pct;
  const fadeTxt =
    fade == null
      ? '—'
      : fade <= 2
        ? 'holding'
        : `${Math.round(fade)}% fade`;
  let onset = '';
  if (d.onset_m) {
    onset = ` · durable to ${(d.onset_m / M_PER_MILE).toFixed(1)} mi`;
  }
  return `${fadeTxt}${onset} · ${d.n} longs`;
}

function hrrLine(h) {
  const bpm = h && (h.drop_60_est != null ? h.drop_60_est : h.drop_60);
  if (!h || !h.usable || bpm == null) {
    return 'Need a 60 s stand, watch on.';
  }
  return `${Math.round(bpm)} bpm`;
}

function statusBits(trainingBands) {
  const bits = [];
  const x = trainingBands.fatigue_hours;
  const g = trainingBands.fitness_g;
  if (typeof x === 'number') {
    bits.push(`leftover load ${x.toFixed(0)} h`);
  }
  if (typeof g === 'number') {
    bits.push(`fitness ${g.toFixed(1)}`);
  }
  return bits.join(' · ');
}

export default function TrainingBandsCard({ trainingBands }) {
  return (
    <CollapsibleCard title="Training status" accentColor={T.color.accentBands}>
      {trainingBands ? (
        <>
          <p style={{ color: T.color.textMuted, marginTop: 0, marginBottom: 8 }}>
            {trainingBands.easy_pace_display
              ? `Easy ${trainingBands.easy_pace_display}${
                  trainingBands.easy_pace_source === 'gap_hr'
                    ? ` from grade-adjusted pace vs heart rate (${trainingBands.easy_pace_n} runs)`
                    : trainingBands.easy_pace_source === 'easy_runs'
                    ? ` from ${trainingBands.easy_pace_n} easy runs`
                    : ['all_runs', 'recent_runs'].includes(trainingBands.easy_pace_source)
                      ? ` from ${trainingBands.easy_pace_n} recent runs`
                      : trainingBands.easy_pace_source === 'profile'
                        ? ' (estimated until you have more runs)'
                        : ''
                }`
              : trainingBands.vdot
                ? `VDOT ${trainingBands.vdot} (${trainingBands.vdot_source === 'vo2_max' ? 'from VO2 max' : 'from recent runs'})`
                : 'Pace needs run history or VO2 max. Heart rate zones still use age and heart rate.'}
            {trainingBands.vdot && trainingBands.vdot_source === 'easy_pace'
              ? ` · VDOT ${trainingBands.vdot}`
              : ''}
            {trainingBands.vdot_watch && trainingBands.pace_source === 'easy_pace'
              ? ` · watch VO2 ${trainingBands.vdot_watch}`
              : ''}
            {trainingBands.lthr ? ` · threshold HR ${trainingBands.lthr}` : ''}
            {trainingBands.hr_max ? ` · max HR ${trainingBands.hr_max}` : ''}
            {trainingBands.hr_rest ? ` · resting HR ${trainingBands.hr_rest}` : ''}
          </p>
          <p style={{ margin: '0 0 8px' }}>
            <strong>
              <InfoBubble label="Durability">{DURABILITY_INFO}</InfoBubble>
            </strong>
            {' · '}
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {durabilityLine(trainingBands.durability)}
            </span>
          </p>
          <p style={{ margin: '0 0 8px' }}>
            <strong>
              <InfoBubble label="1-min drop">{DROP60_INFO}</InfoBubble>
            </strong>
            {' · '}
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {hrrLine(trainingBands.hrr)}
            </span>
          </p>
          {statusBits(trainingBands) ? (
            <p style={{ color: T.color.textMuted, margin: '0 0 10px', fontSize: T.font.base }}>
              {statusBits(trainingBands)}
            </p>
          ) : null}
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '4px 8px 4px 0' }}>Band</th>
                <th style={{ textAlign: 'left', padding: '4px 8px' }}>Pace</th>
                <th style={{ textAlign: 'left', padding: '4px 0' }}>HR</th>
              </tr>
            </thead>
            <tbody>
              {(trainingBands.bands || []).map((band) => (
                <tr key={band.name}>
                  <td style={{ padding: '4px 8px 4px 0' }}>{band.name}</td>
                  <td style={{ padding: '4px 8px', fontVariantNumeric: 'tabular-nums' }}>
                    {band.pace_low && band.pace_high ? `${band.pace_low}–${band.pace_high}` : '—'}
                  </td>
                  <td style={{ padding: '4px 0', fontVariantNumeric: 'tabular-nums' }}>
                    {band.hr_low}–{band.hr_high}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {trainingBands.gap_hr && trainingBands.gap_hr.n_runs >= 6 && (
            <p style={{ color: T.color.textMuted, fontSize: T.font.base }}>
              Grade-adjusted pace vs heart rate
              {` · ${Math.abs(trainingBands.gap_hr.slope_sec_per_bpm)} sec/mile per bpm`}
              {trainingBands.gap_hr.at?.easy?.gap_display
                ? ` · ${trainingBands.gap_hr.at.easy.gap_display} at ${Math.round(trainingBands.gap_hr.at.easy.hr)} bpm`
                : ''}
              {trainingBands.gap_hr.at?.hr_137?.gap_display
                ? ` · ${trainingBands.gap_hr.at.hr_137.gap_display} at 137 bpm`
                : ''}
              {trainingBands.gap_hr.at?.lthr?.gap_display
                ? ` · ${trainingBands.gap_hr.at.lthr.gap_display} at threshold${
                    trainingBands.gap_hr.at.lthr.extrapolated ? ' (extrapolated)' : ''
                  }`
                : ''}
              {` · from ${trainingBands.gap_hr.n_runs} runs`}
            </p>
          )}
          {trainingBands.race_predictions ? (
            <p style={{ marginTop: 10 }}>
              <strong>Race predictions</strong>
              {' · '}
              {trainingBands.race_predictions.summary || '—'}
            </p>
          ) : (
            <p style={{ color: T.color.textLight, fontSize: T.font.sm, marginTop: 10 }}>
              Race predictions will sit here when the model is honest enough to show them.
            </p>
          )}
          <p style={{ color: T.color.textLight, fontSize: T.font.sm }}>{trainingBands.note}</p>
          <p style={{ color: T.color.textLight, fontSize: T.font.sm, marginBottom: 0 }}>
            Durability is second-half fade in GAP vs HR on easy/long runs, not leftover fatigue
            and not easy pace. Quality sessions do not count. 1-min drop: stand 60 s, watch on.
          </p>
        </>
      ) : (
        <p>Set age or VO2 max in Settings, or sync runs, to estimate bands.</p>
      )}
    </CollapsibleCard>
  );
}
